import React, { useMemo, useRef, useCallback, useState } from 'react';
import { DocumentDuplicateIcon, InformationCircleIcon, PencilIcon, TrashIcon, WrenchIcon } from '@heroicons/react/24/outline';
import { createPortal } from 'react-dom';
import { useCapsuleConfigurator } from '../../../../context/CapsuleConfiguratorContext';
import api from '../../../../middleware/api';
import { toast } from 'react-toastify';
import UpdateBatchSensibilityAndDilutionModal from './UpdateBatchSensibilityAndDilutionModal';
import PortalModal from '../../../Common/Modal/PortalModal';
import UpdateMultipleCapsuleModal from './UpdateMultipleCapsuleModal';
import { formatDefaultConfigurationToAddCapsule } from '../../../../utils/capsule_configuration.utils';
import UpdateBatchDilutionModal from './UpdateBatchDilutionModal';
import UpdateBatchSensibilityModal from './UpdateBatchSensibilityModal';
import { ArrowDownAZ, GlassWater, TrendingUpDownIcon } from 'lucide-react';

const SelectionActions = ({ capsulesDropdownOptions, isDivisionMode, removeBatchRows, updateBatchCapsule, isInitialConfiguration }) => {
    const { contentConfiguration: configurations, configurationSelection: selection, setConfigurationSelection, selectedConfigurationId, setCapsuleConfiguration, defaultCapsule, tableContext } = useCapsuleConfigurator();
    const selectedItem = useMemo(() => {
        if (!selection || selection.length === 0) return [];
        // Les ids sont les clés de l'objet selection
        return Object.keys(selection).map(Number);
    }, [selection, configurations]);

    const dilutionModalRef = useRef(null);
    const sensibilityModalRef = useRef(null);
    const [selectedCapsule, setSelectedCapsule] = useState(capsulesDropdownOptions[0]?.id);

    const handleDeleteMultipleMixtures = useCallback(async () => {
        try {
            const mixtureIds = selectedItem.map(item => item.mixture.id);
            const response = await api.put('/capsules/configurations/delete/multiple/mixture', { configurationId: selectedConfigurationId, mixtureIds });
            if (response.status === 200) {
                const updatedCapsule = formatDefaultConfigurationToAddCapsule(response.data.configuration, defaultCapsule.name, defaultCapsule.id);
                removeBatchRows(Object.keys(selection).map(Number));
                setCapsuleConfiguration(updatedCapsule);
                toast.success('Les mélanges ont été supprimés avec succès');
            }
        } catch (e) {
            console.error(e);
            toast.error('Une erreur s\'est produite lors de la suppression des mélanges');
        }
    }, [selectedItem, selectedConfigurationId, selection, defaultCapsule?.name, defaultCapsule?.id]);

    const handleCapsuleSelection = (capsuleId) => {
        const capsule = capsulesDropdownOptions.find(capsule => capsule.id === capsuleId);
        if (capsule && selection) {
            updateBatchCapsule(Object.keys(selection).map(Number), capsule);
            setConfigurationSelection({});
        }
    };

    const handleDilutionModal = () => {
        if (dilutionModalRef.current) {
            if (dilutionModalRef.current.isOpen()) {
                dilutionModalRef.current.hideModal();
            } else {
                dilutionModalRef.current.showModal();
            }
        }
    };

    const handleSensibilityModal = () => {
        if (sensibilityModalRef.current) {
            if (sensibilityModalRef.current.isOpen()) {
                sensibilityModalRef.current.hideModal();
            } else {
                sensibilityModalRef.current.showModal();
            }
        }
    };

    return createPortal(
        <div className='w-auto p-3 bg-gray-100 text-gray-800 shadow-xl rounded-lg fixed bottom-4 left-[80px] border border-gray-300 z-50'>
            <div className='flex flex-col space-y-3 items-center'>
                <p className='text-xs font-bold'>({selectedItem.length})</p>
                {!isInitialConfiguration && <div className="tooltip tooltip-right tooltip-error text-white flex items-center justify-center" data-tip="Supprimer les mélanges sélectionés">
                    <TrashIcon onClick={handleDeleteMultipleMixtures} className='w-6 h-6 cursor-pointer text-white hover:text-red-300 hover:scale-125 transition-all duration-300 bg-red-500 p-1 rounded-full shadow-md flex-shrink-0' />
                </div>}
                {/* <div className="tooltip tooltip-right tooltip-accent text-white flex items-center justify-center" data-tip="Dupliquer les mélanges sélectionés">
                    <DocumentDuplicateIcon onClick={handleDuplicateMultipleMixtures} className='w-6 h-6 cursor-pointer text-white hover:text-blue-200 hover:scale-125 transition-all duration-300 bg-blue-600 p-1 rounded-full shadow-md flex-shrink-0' />
                </div> */}
                {/* <div  className="tooltip tooltip-right tooltip-green flex items-center justify-center" data-tip="Afficher les réactifs pour la sélection">
                    <InformationCircleIcon onClick={handleEditionModal} className='w-6 h-6 cursor-pointer text-white hover:text-green-200 hover:scale-125 transition-all duration-300 bg-green-600 p-1 rounded-full shadow-md flex-shrink-0' />
                </div> */}
                <div className="tooltip tooltip-right tooltip-green   flex items-center justify-center" data-tip="Editez la sensiblité pour la sélection">
                    <TrendingUpDownIcon onClick={handleSensibilityModal} className='w-6 h-6 cursor-pointer text-white hover:text-green-200 hover:scale-125 transition-all duration-300 bg-green-500 p-1 rounded-full shadow-md flex-shrink-0' />
                </div>
                {!isInitialConfiguration && <div className="tooltip tooltip-right tooltip-warning flex items-center justify-center" data-tip="Editez la dilution pour la sélection">
                    <GlassWater onClick={handleDilutionModal} className='w-6 h-6 cursor-pointer text-white hover:text-yellow-200 hover:scale-125 transition-all duration-300 bg-amber-500 p-1 rounded-full shadow-md flex-shrink-0' />
                </div>}
                {isDivisionMode && (
                    <div className="dropdown dropdown-top">
                        <label tabIndex={0} className="tooltip tooltip-right tooltip-purple flex items-center justify-center cursor-pointer" data-tip="Assignez une Capsule à la sélection">
                            <ArrowDownAZ className='w-6 h-6 text-white hover:text-purple-200 hover:scale-125 transition-all duration-300 bg-purple-600 p-1 rounded-full shadow-md flex-shrink-0' />
                        </label>
                        <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52 mt-4">
                            {capsulesDropdownOptions.map((option) => (
                                <li key={option.id}>
                                    <a onClick={() => handleCapsuleSelection(option.id)}>
                                        {option.name}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <UpdateBatchDilutionModal
                ref={dilutionModalRef}
                selectedItem={selectedItem}
                selectedConfigurationId={selectedConfigurationId}
                defaultCapsule={defaultCapsule}
                setCapsuleConfiguration={setCapsuleConfiguration}
            />

            <UpdateBatchSensibilityModal
                ref={sensibilityModalRef}
                selectedItem={selectedItem}
                selectedConfigurationId={selectedConfigurationId}
                defaultCapsule={defaultCapsule}
                setCapsuleConfiguration={setCapsuleConfiguration}
            />
        </div>,
        document.body
    );
};

export default SelectionActions;
